import type { EntityState } from '@reduxjs/toolkit';
import type { AppId } from 'constants/known-apps';
import type { XhrStatus } from 'redux/reusable/xhrStatus';
import { createXhrStatusModule } from 'redux/reusable/xhrStatus';
import type { App, AppAccount, AppSubscription } from 'types/apps';
import type { DeepPartial, ValueOf } from 'types/utility';
import type { Action, ActionCreator, ActionCreatorFactory, Meta } from 'typescript-fsa';
import { actionCreatorFactory } from 'typescript-fsa';

export const { wrapFetching, wrapSaving, withXhrStatusReducer } = createXhrStatusModule('apps');

const actionCreator = actionCreatorFactory('timely/apps');
export const fetchedApps = actionCreator<Array<App>>('FETCHED_APPS');
export const removeApp = actionCreator<number>('REMOVE_APP');

export const fetchedAccounts = actionCreator<{
  appId: number;
  accounts: Array<AppAccount>;
}>('FETCHED_ACCOUNTS');
export const addAccount = actionCreator<{
  appId: number;
  account: AppAccount;
}>('ADD_ACCOUNT');
export const removeAccount = actionCreator<{
  appId: number;
  accountId: number;
  disconnected: boolean;
}>('REMOVE_ACCOUNT');
export const updateAccount = actionCreator<{
  appId: number;
  appAccountId: number;
  patch: DeepPartial<AppAccount>;
}>('UPDATE_ACCOUNT');

export const fetchedSubscriptions = actionCreator<{
  accountId: number;
  subscriptions: Array<AppSubscription>;
}>('FETCHED_SUBSCRIPTIONS');
export const updateDraft = actionCreator<Partial<DraftState>>('UPDATE_DRAFT');
export const updateDraftSubscription = actionCreator<{
  id: string;
  value: Partial<AppSubscription>;
}>('UPDATE_DRAFT_SUBSCRIPTION');

// TODO: Put these generic types in util

interface GenericActionCreator extends ActionCreator<unknown> {
  <P>(payload: P, meta?: Meta): Action<P>;
}

interface GenericActionCreatorFactory extends ActionCreatorFactory {
  (type: string, commonMeta?: Meta, isError?: boolean): GenericActionCreator;
}

function genericActionCreatorFactory(actionCreator: ActionCreatorFactory) {
  return actionCreator as GenericActionCreatorFactory;
}

const genericActionCreator = genericActionCreatorFactory(actionCreator);

export const updateDraftOptions = genericActionCreator('UPDATE_DRAFT_OPTIONS');

export const toggleDraftSubscriptionIntegrated = actionCreator<{
  id: string;
}>('TOGGLE_DRAFT_SUBSCRIPTION_INTEGRATED');
export const updateSubscriptions = actionCreator<{
  accountId: number;
  subscriptions: Array<AppSubscription>;
}>('UPDATE_SUBSCRIPTIONS');

export const updateSearchFilter = actionCreator<string>('UPDATE_SEARCH_FILTER');
export const toggleAppsListModal = actionCreator('TOGGLE_APPS_LIST_MODAL');
export const toggleAppEditor = actionCreator<number | AppId | null | undefined>(
  'TOGGLE_APP_EDITOR'
);
export const toggleAccountEditor = actionCreator<number | null | undefined>(
  'TOGGLE_ACCOUNT_EDITOR'
);
export const setImportStatusFinished = actionCreator<{ appId: number; appAccountId: number }>(
  'SET_IMPORT_STATUS_FINISHED'
);

export const initImportWizard = actionCreator<
  Pick<ImportWizardState, 'jobId'> & { entities: Array<ImportEntity> }
>('INIT_IMPORT_WIZARD');

export const setImportWizardStatus = actionCreator<ImportStatus>('SET_IMPORT_WIZARD_STATUS');

export const resetImportWizard = actionCreator('RESET_IMPORT_WIZARD');

export const toggleSelectedEntityIds = actionCreator<Array<ImportEntity['id']>>(
  'IMPORT_WIZARD_TOGGLE_SELECTED_ENTITY_IDS'
);

export const clickUpSourceOptions = {
  'space-folder': {
    client_source: 'space',
    project_source: 'folder',
  },
  'space-list': {
    client_source: 'space',
    project_source: 'list',
  },
  'folder-list': {
    client_source: 'folder',
    project_source: 'list',
  },
  space: {
    client_source: null,
    project_source: 'space',
  },
  folder: {
    client_source: null,
    project_source: 'folder',
  },
  list: {
    client_source: null,
    project_source: 'list',
  },
} as const;

export type ClickUpSourceOptionKey = keyof typeof clickUpSourceOptions;

export const DEFAULT_CLICKUP_SOURCE: ClickUpSourceOptionKey = 'space-folder';

export const mondaySourceOptions = {
  'board-group': {
    client_source: 'board',
    project_source: 'group',
  },
  board: {
    client_source: null,
    project_source: 'board',
  },
} as const;

export enum SyncOptions {
  SYNC_EVERYTHING_FROM_WORKSPACE,
  SYNC_FROM_ENTITY,
  SYNC_EVERYTHING,
}

export type MondaySourceOptionKey = keyof typeof mondaySourceOptions;

export const DEFAULT_MONDAY_SOURCE: MondaySourceOptionKey = 'board-group';

export type ClickUpSourceOptions = ValueOf<typeof clickUpSourceOptions> & {
  parent_source: 'parent';
};
export type MondaySourceOptions = ValueOf<typeof mondaySourceOptions> & {
  parent_source: 'workspace';
};

export type AppImportOptions<
  TSource =
    | ClickUpSourceOptions
    | MondaySourceOptions
    | { parent_source: 'project' }
    | { parent_source: 'space' }
    | { parent_source: 'board' },
> = TSource & {
  sync_all: boolean;
};

export type ClickUpImportOptions = AppImportOptions<ClickUpSourceOptions>;
export type MondayImportOptions = AppImportOptions<MondaySourceOptions>;

export interface ImportEntity {
  id: number | string;
  name: string;
  parent_name?: string;
}

export enum ImportStatus {
  IDLE = 'idle',
  INITIALIZED = 'initialized',
  IN_PROGRESS = 'in_progress',
  ERROR = 'error',
}
export interface ImportWizardState {
  status: ImportStatus;
  entities: EntityState<ImportEntity, string | number>;
  selectedEntities: Array<ImportEntity['id']>;
  jobId: number | null;
}

export interface State {
  accounts: {
    [appId: number]: Array<AppAccount>;
  };
  appAccountEditorId: number | null;
  appEditorId: number | null | undefined;
  appsEntities: {
    [appId: number]: App;
  };
  appsResult: Array<number>;
  draft: {
    options: AppImportOptions | null;
    subscriptions: Array<AppSubscription> | null | undefined;
  };

  import: ImportWizardState;
  filter: {
    search: string;
  };
  showAppsListModal: boolean;
  subscriptions: {
    [id: number]: Array<AppSubscription>;
  };
  xhrStatus: XhrStatus;
}

export type DraftState = State['draft'];
